import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

type Slide = {
  tag: string;
  title: string;
  date: string;
  originalPrice: string;
  discountedPrice: string;
  saving?: string;
  link: string;
  bg: string;
  btnColor: string;
};

const SLIDES: Slide[] = [
  {
    tag: "★ Most Popular",
    title: "Ausbildung\nPreparatory Course",
    date: "Aug 1, 2026  ·  Deadline: Jul 25",
    originalPrice: "2,25,000 BDT",
    discountedPrice: "1,56,000 BDT",
    saving: "Save 69,000 BDT",
    link: "/course-details/ausbildung-preparatory-course",
    bg: "from-[#006fa8] via-[#009fe3] to-[#33b8f5]",
    btnColor: "text-[#006fa8]",
  },
  {
    tag: "German Language · A1",
    title: "German Language\nCourse — A1",
    date: "Aug 1, 2026  ·  Deadline: Jul 25",
    originalPrice: "18,500 BDT",
    discountedPrice: "12,500 BDT",
    link: "/course-details/german-language-course",
    bg: "from-[#0088c8] via-[#00aee8] to-[#33c8f8]",
    btnColor: "text-[#0088c8]",
  },
  {
    tag: "German Language · A2",
    title: "German Language\nCourse — A2",
    date: "Aug 1, 2026  ·  Deadline: Jul 25",
    originalPrice: "19,500 BDT",
    discountedPrice: "13,500 BDT",
    link: "/course-details/german-language-course",
    bg: "from-[#005888] via-[#0078b8] to-[#009fe3]",
    btnColor: "text-[#005888]",
  },
  {
    tag: "German Language · B1",
    title: "German Language\nCourse — B1",
    date: "Aug 15, 2026  ·  Deadline: Aug 10",
    originalPrice: "23,500 BDT",
    discountedPrice: "15,500 BDT",
    link: "/course-details/german-language-course",
    bg: "from-[#00796b] via-[#009e8c] to-[#26c2ae]",
    btnColor: "text-[#00796b]",
  },
  {
    tag: "German Language · B2",
    title: "German Language\nCourse — B2",
    date: "Aug 1, 2026  ·  Deadline: Jul 25",
    originalPrice: "25,000 BDT",
    discountedPrice: "17,500 BDT",
    link: "/course-details/german-language-course",
    bg: "from-[#005c4e] via-[#007a6b] to-[#009e8c]",
    btnColor: "text-[#005c4e]",
  },
];

const DURATION = 4000;

const CalIcon = () => (
  <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

const BannerCarousel = ({ height }: { height: string }) => {
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pausedRef = useRef(false);
  const total = SLIDES.length;

  const goTo = (n: number) => {
    setCurrent(((n % total) + total) % total);
    resetProgress();
  };

  const resetProgress = () => {
    if (progressRef.current) clearInterval(progressRef.current);
    setProgress(0);
    progressRef.current = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) { clearInterval(progressRef.current!); return 100; }
        return p + 100 / (DURATION / 100);
      });
    }, 100);
  };

  const startAuto = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      if (!pausedRef.current) setCurrent((c) => (c + 1) % total);
    }, DURATION);
    resetProgress();
  };

  useEffect(() => {
    startAuto();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, []);

  const s = SLIDES[current];

  return (
    <div
      className={`relative overflow-hidden ${height} w-full`}
      onMouseEnter={() => { pausedRef.current = true; if (progressRef.current) clearInterval(progressRef.current); }}
      onMouseLeave={() => { pausedRef.current = false; startAuto(); }}
    >
      {/* Slide background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${s.bg} transition-all duration-500`} />

      {/* Deco circles */}
      <div className="absolute w-56 h-56 rounded-full bg-white/[.07] -top-12 -right-10 pointer-events-none" />
      <div className="absolute w-28 h-28 rounded-full bg-white/[.05] bottom-6 left-1/3 pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 flex flex-row items-center justify-between h-full px-4 sm:px-7 py-4 sm:py-6 gap-2 sm:gap-4">
        {/* Left */}
        <div className="flex flex-col gap-1.5 sm:gap-3 flex-1 min-w-0">
          <div className="inline-block self-start bg-white/20 text-white text-[9px] sm:text-[10px] font-bold uppercase tracking-widest px-2 sm:px-3 py-0.5 sm:py-1 rounded-full">
            {s.tag}
          </div>
          <div className="text-white font-extrabold text-base sm:text-2xl leading-tight whitespace-pre-line">
            {s.title}
          </div>
          <div className="flex items-center gap-1.5 bg-white/25 text-white text-[9px] sm:text-sm font-semibold px-2 sm:px-3 py-1 sm:py-1.5 rounded-full self-start shadow-sm whitespace-nowrap">
            <CalIcon />
            {s.date}
          </div>
          <Link
            to={s.link}
            className={`self-start bg-white text-xs sm:text-sm font-black px-3 sm:px-5 py-1.5 sm:py-2 rounded-lg sm:rounded-xl shadow-md hover:-translate-y-0.5 hover:shadow-lg transition-all ${s.btnColor}`}
          >
            Enroll Now →
          </Link>
        </div>

        {/* Right — Price card */}
        <div className="flex-shrink-0 bg-white/15 border border-white/25 rounded-xl sm:rounded-2xl px-3 sm:px-5 py-2.5 sm:py-4 text-right backdrop-blur-sm">
          <div className="flex items-center justify-end gap-1.5 mb-0.5 sm:mb-1">
            <span className="text-white/60 text-[10px] sm:text-xs line-through">{s.originalPrice}</span>
            <span className="bg-red-500 text-white text-[8px] sm:text-[9px] font-black px-1.5 sm:px-2 py-0.5 rounded-full">30% OFF</span>
          </div>
          <div className="text-white font-black text-xl sm:text-3xl leading-none">
            {s.discountedPrice.replace(" BDT", "")}
          </div>
          <div className="text-white/70 text-[10px] sm:text-xs mt-0.5 font-semibold tracking-wide">BDT</div>
          {s.saving && (
            <div className="hidden sm:block text-white/80 text-[10px] mt-2 bg-white/10 px-2 py-1 rounded-full">💰 {s.saving}</div>
          )}
        </div>
      </div>

      {/* Trust badge */}
      <div className="absolute bottom-2 left-4 z-20 flex items-center gap-1 text-white/70 text-[9px] sm:text-[10px] font-semibold">
        🇩🇪 Trusted since 2021
      </div>

      {/* Dots with progress */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
        {SLIDES.map((_, i) => (
          i === current ? (
            <button key={i} onClick={() => goTo(i)} className="relative h-[6px] w-14 rounded-full bg-white/30 overflow-hidden">
              <div className="absolute left-0 top-0 h-full bg-white rounded-full transition-[width] duration-100" style={{ width: `${progress}%` }} />
            </button>
          ) : (
            <button key={i} onClick={() => goTo(i)} className="h-[6px] w-[6px] rounded-full bg-white/40 transition-all duration-300" />
          )
        ))}
      </div>
    </div>
  );
};

const Banner = () => {
  const { t } = useTranslation();

  return (
    <section className="bg-gray-50 py-4 font-sans">
      <div className="max-w-7xl mx-auto px-2 sm:px-4">
        <div className="bg-white/90 backdrop-blur-lg shadow-2xl rounded-md overflow-hidden flex flex-col lg:flex-row items-stretch transition-all duration-300 hover:shadow-3xl">

          {/* Mobile Carousel */}
          <div className="lg:hidden w-full">
            <BannerCarousel height="h-[260px] sm:h-[320px]" />
          </div>

          {/* Text Content */}
          <div className="w-full lg:w-1/2 p-6 sm:p-8 lg:p-14 space-y-7" data-aos="fade-up">
            <div className="space-y-4">
              <h6 className="text-sm font-semibold text-[#009fe3] uppercase tracking-wider">
                {t("banner.tagline")}
              </h6>
              <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight">
                {t("banner.headline1")}
                <span className="block mt-2 bg-gradient-to-r from-[#009fe3] to-[#00b4d8] bg-clip-text text-transparent">
                  {t("banner.headline2")}
                </span>
              </h1>
              <p className="text-lg text-gray-700 leading-relaxed">
                {t("banner.description")}
              </p>
            </div>
            <div>
              <Link
                to="/course/courses"
                className="w-full inline-block bg-[#009fe3] hover:bg-[#007bbf] text-white font-semibold text-lg py-3 px-8 rounded-lg shadow transition duration-300 text-center"
                aria-label="Explore Our Courses"
              >
                {t("banner.cta")}
              </Link>
            </div>
          </div>

          {/* Desktop Carousel */}
          <div className="hidden lg:block w-full lg:w-1/2 lg:mr-8 lg:mt-8 lg:mb-14 rounded-2xl overflow-hidden shadow-lg">
            <BannerCarousel height="h-full min-h-[380px]" />
          </div>

        </div>
      </div>
    </section>
  );
};

export default Banner;
